import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Field, SetupEditor, SetupPage } from './setup-shell';
import {
  useCreateServiceType,
  useDeleteServiceType,
  useServiceTypes,
  useUpdateServiceType,
  type ServiceTypeRecord,
} from './setup.api';

/**
 * Service types — a name in each language, and nothing else.
 *
 * Both languages are always shown rather than only the one matching the
 * current locale: this page is where they are maintained, and a list that
 * hides the other language is how a missing translation goes unnoticed.
 */

const EMPTY = { nameEn: '', nameEs: '' };

export function ServiceTypesPage() {
  const { t } = useTranslation();

  const list = useServiceTypes();
  const create = useCreateServiceType();
  const update = useUpdateServiceType();
  const remove = useDeleteServiceType();

  const [editing, setEditing] = useState<ServiceTypeRecord | null>(null);
  const [draft, setDraft] = useState(EMPTY);

  const rows = list.data ?? [];
  const isEdit = !!editing?.id;
  const saving = create.isPending || update.isPending;

  function open(row: ServiceTypeRecord | null) {
    setEditing(row ?? ({ id: '', nameEn: '', nameEs: '' } as ServiceTypeRecord));
    setDraft({ nameEn: row?.nameEn ?? '', nameEs: row?.nameEs ?? '' });
  }

  const failed = () => toast.error(t('bookingSetup.actionFailed'));
  const saved = () => {
    toast.success(t('bookingSetup.saved'));
    setEditing(null);
  };

  function save() {
    if (isEdit) {
      update.mutate(
        { id: editing!.id, ...draft },
        { onSuccess: saved, onError: failed }
      );
    } else {
      create.mutate(draft, { onSuccess: saved, onError: failed });
    }
  }

  return (
    <SetupPage
      title={t('bookingSetup.serviceTypesTitle')}
      description={t('bookingSetup.serviceTypesSubtitle')}
      onCreate={() => open(null)}
      onReload={() => void list.refetch()}
      reloading={list.isFetching}
      isPending={list.isPending}
      isError={list.isError}
      errorMessage={t('bookingSetup.loadFailed')}
      isEmpty={list.isSuccess && rows.length === 0}
      emptyMessage={t('bookingSetup.noServiceTypes')}
    >
      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              list.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between gap-3 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{row.nameEn || '—'}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    {row.nameEs || '—'}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => open(row)}>
                  <Pencil className="size-3.5" />
                  {t('bookingSetup.edit')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <SetupEditor
        title={
          isEdit
            ? t('bookingSetup.editServiceType')
            : t('bookingSetup.newServiceType')
        }
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        onSave={save}
        saving={saving}
        canSave={!!draft.nameEn.trim() || !!draft.nameEs.trim()}
        isEdit={isEdit}
        deleting={remove.isPending}
        deletePrompt={t('bookingSetup.deleteServiceTypePrompt')}
        onDelete={() =>
          remove.mutate(editing!.id, {
            onSuccess: () => {
              toast.success(t('bookingSetup.deleted'));
              setEditing(null);
            },
            onError: failed,
          })
        }
      >
        <Field label={`${t('bookingSetup.name')} (EN)`}>
          <Input
            value={draft.nameEn}
            onChange={(e) => setDraft({ ...draft, nameEn: e.target.value })}
          />
        </Field>
        <Field label={`${t('bookingSetup.name')} (ES)`}>
          <Input
            value={draft.nameEs}
            onChange={(e) => setDraft({ ...draft, nameEs: e.target.value })}
          />
        </Field>
      </SetupEditor>
    </SetupPage>
  );
}
